import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { LuDownload, LuLoader, LuChurch, LuBuilding2, LuCalendarDays, LuRefreshCw } from 'react-icons/lu';
import '../styles/InformesDocumentos.css';

// ═══════════════════════════════════════════════════════════════════════════
//  UTILIDADES
// ═══════════════════════════════════════════════════════════════════════════

const fmt = (n) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(n ?? 0);

const NOMBRES_MESES_ES = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
const NOMBRES_MESES_FULL = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const NOMBRES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const mesLabel = (key) => { const [y,m] = key.split('-'); return `${NOMBRES_CORTOS[+m-1]} ${y}`; };
const sortMeses = (arr) => [...arr].sort();
const ultimoDia = (key) => { const [y,m] = key.split('-'); return new Date(+y, +m, 0).getDate(); };

// Nombre de hoja al estilo "MAR_2026" o "Mar 2026"
const sheetNameDistrito = (key) => { const [y,m] = key.split('-'); return `${NOMBRES_MESES_ES[+m-1]}_${y}`; };
const sheetNameIglesia  = (key) => { const [y,m] = key.split('-'); return `${NOMBRES_CORTOS[+m-1]} ${y}`; };

// Año eclesiástico: oct a sep. Devuelve las claves "YYYY-MM" en orden cronológico.
function mesesAnioEclesiastico(anioInicio) {
  const out = [];
  // oct, nov, dic del año anterior al "fin"
  for (let m = 10; m <= 12; m++) out.push(`${anioInicio}-${String(m).padStart(2,'0')}`);
  for (let m = 1; m <= 9; m++)   out.push(`${anioInicio + 1}-${String(m).padStart(2,'0')}`);
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
//  SALDOS INICIALES DEL AÑO ECLESIÁSTICO
//  Se guardan en localStorage por año eclesiástico (clave: 'saldos_YYYY_TIPO_KEY')
//  Tipo: 'distrito' o 'iglesia_<CodLocal>'
// ═══════════════════════════════════════════════════════════════════════════

const SALDOS_DEFAULT_DISTRITO = {
  // C7, C8, C9 (Distrito sección)
  distrito_c7: 0, distrito_c8: 0, distrito_c9: 0,
  // C14, C15, C16 (MNI)
  mni_c14: 0, mni_c15: 0, mni_c16: 0,
  // C21, C22, C23 (DNI)
  dni_c21: 0, dni_c22: 0, dni_c23: 0,
  // C28, C29 (JNI)
  jni_c28: 0, jni_c29: 0,
  // Saldo total resumen (C35 del primer mes)
  resumen: 0,
};

const SALDOS_DEFAULT_IGLESIA = {
  iglesia: 0,    // C5 (Saldo Mes Anterior Iglesia)
  mni: 0,        // H5 (Saldo MNI)
  dni: 0,        // C27
  jni: 0,        // C41
  minMujer: 0,   // H21 (X0)
  compasion: 0,  // H28 (Y0)
  caja: 0,       // C61 (RESUMEN CAJA Saldo cta cte anterior)
};

const claveSaldosDistrito = (anioInicio) => `saldos_${anioInicio}_distrito`;
const claveSaldosIglesia  = (anioInicio, codLocal) => `saldos_${anioInicio}_iglesia_${codLocal}`;

function leerSaldosDistrito(anioInicio) {
  try {
    const raw = localStorage.getItem(claveSaldosDistrito(anioInicio));
    if (!raw) return { ...SALDOS_DEFAULT_DISTRITO };
    return { ...SALDOS_DEFAULT_DISTRITO, ...JSON.parse(raw) };
  } catch { return { ...SALDOS_DEFAULT_DISTRITO }; }
}
function guardarSaldosDistrito(anioInicio, saldos) {
  try { localStorage.setItem(claveSaldosDistrito(anioInicio), JSON.stringify(saldos)); }
  catch (e) { console.error('No se pudo guardar saldos:', e); }
}
function leerSaldosIglesia(anioInicio, codLocal) {
  try {
    const raw = localStorage.getItem(claveSaldosIglesia(anioInicio, codLocal));
    if (!raw) return { ...SALDOS_DEFAULT_IGLESIA };
    return { ...SALDOS_DEFAULT_IGLESIA, ...JSON.parse(raw) };
  } catch { return { ...SALDOS_DEFAULT_IGLESIA }; }
}
function guardarSaldosIglesia(anioInicio, codLocal, saldos) {
  try { localStorage.setItem(claveSaldosIglesia(anioInicio, codLocal), JSON.stringify(saldos)); }
  catch (e) { console.error('No se pudo guardar saldos:', e); }
}

// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
//  ESTILOS — Times New Roman en títulos, Calibri en datos
// ═══════════════════════════════════════════════════════════════════════════

const NF_MONEDA = '_ "$"* #,##0_ ;_ "$"* -#,##0_ ;_ "$"* "-"_ ;_ @_ ';
const BORDE_MEDIO = { style: 'medium', color: { rgb: '000000' } };
const BORDE_FINO  = { style: 'thin',   color: { rgb: '000000' } };

const S = {
  // títulos del encabezado (filas 1-3 del original) — Times New Roman, único uso
  tituloPrincipal: {
    font: { name: 'Times New Roman', sz: 14, bold: true, color: { rgb: '000000' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  tituloMes: {
    font: { name: 'Times New Roman', sz: 12, bold: true, color: { rgb: '000000' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  // encabezado pequeño (año, mes, pr.dia, ult.dia)
  encabezadoMini: {
    font: { name: 'Calibri', sz: 8, bold: true },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
    fill: { fgColor: { rgb: 'FFF2CC' } },
  },
  encabezadoMiniVal: {
    font: { name: 'Calibri', sz: 9, bold: false },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
    fill: { fgColor: { rgb: 'FFF2CC' } },
  },
  // banda de sección (DISTRITO BIO BIO, MNI, DNI, JNI) — Times bold
  seccion: {
    font: { name: 'Times New Roman', sz: 11, bold: true, color: { rgb: '000000' } },
    fill: { fgColor: { rgb: 'D9E1F2' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_MEDIO, right: BORDE_MEDIO },
  },
  // encabezados de columna (SALDO MES ANTERIOR | INGRESOS | GASTOS | SALDO | OBS)
  thCol: {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: '000000' } },
    fill: { fgColor: { rgb: 'FFF2CC' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_MEDIO, right: BORDE_MEDIO },
  },
  // descripción de fila (col B)
  desc: {
    font: { name: 'Calibri', sz: 10, bold: false },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_MEDIO, right: BORDE_FINO },
  },
  // descripción en negrita
  descBold: {
    font: { name: 'Calibri', sz: 10, bold: true },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  // valores moneda dentro de filas
  moneda: {
    font: { name: 'Calibri', sz: 10, bold: false },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  // fila TOTALES
  totalLbl: {
    font: { name: 'Calibri', sz: 10, bold: true },
    fill: { fgColor: { rgb: 'E7E6E6' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_MEDIO, right: BORDE_MEDIO },
  },
  totalVal: {
    font: { name: 'Calibri', sz: 10, bold: true },
    fill: { fgColor: { rgb: 'E7E6E6' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_MEDIO, right: BORDE_MEDIO },
  },
  // resumen final
  resumenLbl: {
    font: { name: 'Calibri', sz: 10, bold: true },
    fill: { fgColor: { rgb: 'FFF2CC' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  resumenVal: {
    font: { name: 'Calibri', sz: 10, bold: true },
    fill: { fgColor: { rgb: 'FFF2CC' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  // firma
  firma: {
    font: { name: 'Calibri', sz: 10, italic: true },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  // observación (columna G)
  obs: {
    font: { name: 'Calibri', sz: 9 },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_MEDIO },
  },

  // ── estilos para hojas auxiliares (más modernos) ──
  thAux: {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '1A3A5C' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  tdAux: {
    font: { name: 'Calibri', sz: 10 },
    alignment: { vertical: 'center', wrapText: true },
    border: { bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  tdAuxMoney: {
    font: { name: 'Calibri', sz: 10 },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
  tdAuxTotal: {
    font: { name: 'Calibri', sz: 11, bold: true },
    fill: { fgColor: { rgb: 'E7E6E6' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_FINO, right: BORDE_FINO },
  },
  tdAuxTotalLbl: {
    font: { name: 'Calibri', sz: 11, bold: true },
    fill: { fgColor: { rgb: 'E7E6E6' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_FINO, right: BORDE_FINO },
  },
  tituloAux: {
    font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: '1A3A5C' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  seccionAux: {
    font: { name: 'Calibri', sz: 12, bold: true, color: { rgb: '1A3A5C' } },
    fill: { fgColor: { rgb: 'D6E4F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
  },
  porcentaje: {
    font: { name: 'Calibri', sz: 10 },
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0.0%',
    border: { bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
//  HELPERS DE CELDA
// ═══════════════════════════════════════════════════════════════════════════

const cv  = (v, s) => ({ v, s, t: typeof v === 'number' ? 'n' : 's' });
const cf  = (f, s) => ({ f, s, t: 'n' });   // fórmula
const cvc = (v, s) => ({ v, s, t: 'n' });   // valor numérico con estilo
const cell = (r, c) => XLSX.utils.encode_cell({ r, c });

function fila(ws, r, celdas) {
  celdas.forEach((cel, c) => { if (cel != null) ws[cell(r, c)] = cel; });
}
function addMerges(ws, merges) {
  ws['!merges'] = (ws['!merges'] || []).concat(
    merges.map(([r1,c1,r2,c2]) => ({ s:{r:r1,c:c1}, e:{r:r2,c:c2} }))
  );
}
function setRef(ws, maxR, maxC) {
  ws['!ref'] = XLSX.utils.encode_range({ r: 0, c: 0 }, { r: maxR, c: maxC });
}

// Configuración de impresión. xlsx-js-style NO escribe <pageSetup> en el XML,
// así que guardamos las opciones aquí en la hoja y las inyectamos después con
// JSZip dentro de `descargarConPrintSetup()`.
function setPrint(ws, opts = {}) {
  ws['!margins'] = {
    left: 0.3, right: 0.3, top: 0.4, bottom: 0.4,
    header: 0.2, footer: 0.2,
  };
  // Metadatos para inyección posterior (no son leídos por xlsx-js-style, sólo por nosotros)
  ws.__printOpts = {
    orientation:  opts.orientation || 'landscape',
    paperSize:    9,        // A4
    fitToWidth:   1,
    fitToHeight:  opts.fitToHeight ?? 0,
    scale:        opts.scale,
    horizontalCentered: true,
    printArea:    opts.printArea,
  };
}

// XML para <pageSetup> dentro de la hoja
function pageSetupXml(opts) {
  const parts = [];
  if (opts.paperSize)   parts.push(`paperSize="${opts.paperSize}"`);
  if (opts.scale)       parts.push(`scale="${opts.scale}"`);
  if (opts.fitToWidth !== undefined)  parts.push(`fitToWidth="${opts.fitToWidth}"`);
  if (opts.fitToHeight !== undefined) parts.push(`fitToHeight="${opts.fitToHeight}"`);
  if (opts.orientation) parts.push(`orientation="${opts.orientation}"`);
  return `<pageSetup ${parts.join(' ')}/>`;
}

// XML para <printOptions>
function printOptionsXml(opts) {
  const parts = [];
  if (opts.horizontalCentered) parts.push('horizontalCentered="1"');
  return `<printOptions ${parts.join(' ')}/>`;
}

// XML para <sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>
const SHEET_PR_FIT = `<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>`;

// Post-procesa el .xlsx generado: inyecta pageSetup/printOptions/fitToPage
// en cada hoja que tenga `__printOpts` definido. Devuelve un Blob descargable.
async function postProcesarXlsx(wb, nombresAOpts) {
  // Generar el .xlsx como ArrayBuffer
  const arr = XLSX.write(wb, { type: 'array', bookType: 'xlsx', cellStyles: true });
  const zip = await JSZip.loadAsync(arr);

  // Mapear nombre de hoja → sheetN.xml. El orden de wb.SheetNames coincide con sheet1, sheet2...
  for (let i = 0; i < wb.SheetNames.length; i++) {
    const nombre = wb.SheetNames[i];
    const opts = nombresAOpts[nombre];
    if (!opts) continue;

    const path = `xl/worksheets/sheet${i + 1}.xml`;
    const fileObj = zip.file(path);
    if (!fileObj) continue;

    let xml = await fileObj.async('string');

    // 1. Insertar <sheetPr><pageSetUpPr fitToPage="1"/></sheetPr> al principio del worksheet
    //    si no existe.
    if (!xml.includes('<sheetPr')) {
      xml = xml.replace(/<worksheet([^>]*)>/, `<worksheet$1>${SHEET_PR_FIT}`);
    }

    // 2. Insertar/reemplazar <pageSetup .../> y <printOptions .../> ANTES de <pageMargins>
    //    Si <pageMargins> no existe (raro), agregarlo al final del worksheet.
    const setupXml   = pageSetupXml(opts);
    const optionsXml = printOptionsXml(opts);
    const insertBlock = `${optionsXml}`;
    const setupBlock  = `${setupXml}`;

    // Quitar pageSetup/printOptions previos si existieran (no debería con xlsx-js-style, pero por si acaso)
    xml = xml.replace(/<pageSetup[^/]*\/>/g, '');
    xml = xml.replace(/<printOptions[^/]*\/>/g, '');

    if (xml.includes('<pageMargins')) {
      // Insertar printOptions ANTES de pageMargins y pageSetup DESPUÉS de pageMargins
      xml = xml.replace(/(<pageMargins[^/]*\/>)/, `${insertBlock}$1${setupBlock}`);
    } else {
      // Agregar al final
      xml = xml.replace(/<\/worksheet>/, `${insertBlock}<pageMargins left="0.3" right="0.3" top="0.4" bottom="0.4" header="0.2" footer="0.2"/>${setupBlock}</worksheet>`);
    }

    zip.file(path, xml);
  }

  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

// Descarga un Blob como archivo (reemplaza a XLSX.writeFile)
function descargarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Descarga el workbook con post-procesamiento de pageSetup
async function descargarConPrintSetup(wb, filename) {
  // Construir mapa nombre → __printOpts
  const nombresAOpts = {};
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (ws.__printOpts) nombresAOpts[name] = ws.__printOpts;
  }
  const blob = await postProcesarXlsx(wb, nombresAOpts);
  descargarBlob(blob, filename);
}

// ═══════════════════════════════════════════════════════════════════════════
//  HOJA REgistrosMes — fuente de datos para los SUMIFS
// ═══════════════════════════════════════════════════════════════════════════

const REG_HEADER_ROW = 8;   // fila 8 (1-based) = índice 7 (0-based)
const REG_DATA_START = 9;   // fila 9 (1-based) = índice 8

const COL_REG = {
  ID:          0,
  FECHA:       1,
  NUM_DOC:     2,
  TIPO_DOC:    3,
  MONTO:       4,
  LOCAL:       5,
  CENTRO_COSTO:6,
  GLOSA:       7,
  USUARIO:     8,
  TIPO_REG:    9,
  ESTADO:      10,
  MOTIVO:      11,
  MES:         12,
  ANIO:        13,
};

function construirHojaRegistros(registros) {
  const ws = { '!merges': [] };
  const headerStyle = {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '305496' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  };
  const dataStyle = {
    font: { name: 'Calibri', sz: 10 },
    alignment: { vertical: 'center' },
    border: { bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  };
  const moneyStyle = {
    ...dataStyle,
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
  };
  const dateStyle = {
    ...dataStyle,
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: 'dd-mm-yyyy',
  };

  // Título en la parte superior (filas 1-7 reservadas como en el original)
  ws[cell(0, 4)] = cv('REGISTROS', {
    font: { name: 'Calibri', sz: 16, bold: true, color: { rgb: '1A3A5C' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  });
  addMerges(ws, [[0, 4, 0, 13]]);

  // Encabezados en fila 8 (índice 7)
  const headers = [
    'ID', 'Fecha', 'Número de Documento', 'Tipo de Documento',
    'Monto', 'Local', 'Centro de Costo', 'Glosa', 'Usuario',
    'Tipo de Registro', 'Estado', 'Motivo', 'mes', 'año',
  ];
  headers.forEach((h, c) => { ws[cell(REG_HEADER_ROW - 1, c)] = cv(h, headerStyle); });

  // Datos desde fila 9 (índice 8)
  registros.forEach((reg, i) => {
    const r = (REG_DATA_START - 1) + i;
    const fechaSerial = toExcelDate(reg.fecha);  // número serial de Excel

    ws[cell(r, COL_REG.ID)]           = cvc(reg.id, dataStyle);
    ws[cell(r, COL_REG.FECHA)]        = { v: fechaSerial, s: dateStyle, t: 'n' };
    ws[cell(r, COL_REG.NUM_DOC)]      = cv(String(reg.numDoc || ''), dataStyle);
    ws[cell(r, COL_REG.TIPO_DOC)]     = cv(reg.tipoDoc || '', dataStyle);
    ws[cell(r, COL_REG.MONTO)]        = cvc(reg.monto, moneyStyle);
    ws[cell(r, COL_REG.LOCAL)]        = cv(reg.local || '', dataStyle);
    ws[cell(r, COL_REG.CENTRO_COSTO)] = cv(reg.centroCosto || '', dataStyle);
    ws[cell(r, COL_REG.GLOSA)]        = cv(reg.glosa || '', dataStyle);
    ws[cell(r, COL_REG.USUARIO)]      = cv(reg.usuario || '', dataStyle);
    ws[cell(r, COL_REG.TIPO_REG)]     = cv(reg.tipoReg || '', dataStyle);
    ws[cell(r, COL_REG.ESTADO)]       = cv(reg.estado || 'Activo', dataStyle);
    ws[cell(r, COL_REG.MOTIVO)]       = cv(reg.motivo || '', dataStyle);
    // Columnas mes y año: fórmulas =MONTH(Bn) / =YEAR(Bn)
    ws[cell(r, COL_REG.MES)]  = cf(`MONTH(B${r + 1})`, { ...dataStyle, alignment: { horizontal: 'center', vertical: 'center' } });
    ws[cell(r, COL_REG.ANIO)] = cf(`YEAR(B${r + 1})`,  { ...dataStyle, alignment: { horizontal: 'center', vertical: 'center' } });
  });

  ws['!cols'] = [
    { wch: 8 },   // ID
    { wch: 12 },  // Fecha
    { wch: 18 },  // Num Doc
    { wch: 16 },  // Tipo Doc
    { wch: 14 },  // Monto
    { wch: 22 },  // Local
    { wch: 16 },  // Centro Costo
    { wch: 50 },  // Glosa
    { wch: 12 },  // Usuario
    { wch: 14 },  // Tipo Reg
    { wch: 10 },  // Estado
    { wch: 22 },  // Motivo
    { wch: 6 },   // mes
    { wch: 6 },   // año
  ];

  const maxRow = (REG_DATA_START - 1) + Math.max(registros.length - 1, 0);
  setRef(ws, Math.max(maxRow, REG_HEADER_ROW), 13);
  return ws;
}

// Convierte "YYYY-MM-DD" al serial date de Excel
function toExcelDate(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  // Excel serial date: días desde 1899-12-30
  const ms = Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30);
  return Math.floor(ms / 86400000);
}

// ═══════════════════════════════════════════════════════════════════════════
//  NOMBRES CON RANGO
// ═══════════════════════════════════════════════════════════════════════════

function nombresDeRangoRegistros(totalFilas) {
  const fin = (REG_DATA_START - 1) + Math.max(totalFilas, 1);
  // Las columnas se nombran sin tabla estructurada, apuntando a los rangos directos
  // de la hoja REgistrosMes. xlsx-js-style soporta Names a nivel de workbook.
  const ref = (col) => `REgistrosMes!$${col}$${REG_DATA_START}:$${col}$${fin}`;
  return [
    { Name: 'MONTO',       Ref: ref('E') },
    { Name: 'CentroCosto', Ref: ref('G') },
    { Name: 'TipoReg',     Ref: ref('J') },
    { Name: 'TipoMovto',   Ref: ref('L') },
    { Name: 'MES',         Ref: ref('B') },
  ];
}

// ═══════════════════════════════════════════════════════════════════════════
//  HOJA MENSUAL DISTRITO — replica MAR_2026 (DISTRITO/MNI/DNI/JNI)
// ═══════════════════════════════════════════════════════════════════════════

function construirHojaMensualDistrito(mesKey, mesAntKey, esPrimerMes, saldosIniciales) {
  const ws = { '!merges': [] };
  const [anio, mes] = mesKey.split('-').map(Number);
  const ultDia      = ultimoDia(mesKey);
  const sheetAnt    = mesAntKey ? sheetNameDistrito(mesAntKey) : null;

  // ── Filas 1-3: encabezado ──
  fila(ws, 0, [
    cv('INFORME MENSUAL', S.tituloPrincipal),
    null,null,null,null,null,null,null,null,
    cvc(anio, S.encabezadoMiniVal),
    cvc(mes,  S.encabezadoMiniVal),
    cvc(1,    S.encabezadoMiniVal),
    cvc(ultDia, S.encabezadoMiniVal),
  ]);
  fila(ws, 1, [
    cv('INFORME FINANZAS DISTRITO BIO BIO', S.tituloPrincipal),
    null,null,null,null,null,null,null,null,
    cv('AÑO', S.encabezadoMini), cv('MES', S.encabezadoMini),
    cv('PR.DIA', S.encabezadoMini), cv('ULT. DIA DEL MES', S.encabezadoMini),
  ]);
  fila(ws, 2, [ cv(`MES: ${NOMBRES_MESES_FULL[mes-1]} ${anio}`, S.tituloMes) ]);
  addMerges(ws, [[0,0,0,8], [1,0,1,8], [2,0,2,6]]);

  // ── Sección DISTRITO BIO BIO (fila 5 = índice 4) ──
  fila(ws, 4, [ cv('DISTRITO BIO BIO', S.seccion) ]);
  addMerges(ws, [[4,0,4,6]]);

  // Fila 6 (índice 5): encabezados columnas
  fila(ws, 5, [
    null, null,
    cv('SALDO MES ANTERIOR', S.thCol),
    cv('INGRESOS', S.thCol),
    cv('GASTOS', S.thCol),
    cv('SALDO', S.thCol),
    cv('OBSERVACION DE GASTOS', S.thCol),
  ]);

  // Filas 7-9 (índices 6-8): datos Distrito
  // Las fórmulas son LITERALMENTE las del original.
  const refC = (rDest, rOrigen) => esPrimerMes
    ? cvc(saldosIniciales.distrito[rOrigen] || 0, S.moneda)
    : cf(`+'${sheetAnt}'!F${rOrigen}`, S.moneda);

  // Fila 7 — PPTO Distrito
  fila(ws, 6, [
    null,
    cv('PPTO Distrito aporte IDNs Distrito Bio Bio', S.desc),
    refC(7, 7),
    cf(`SUMIFS(MONTO,CentroCosto,"Distrito",TipoReg,"Ingreso",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"Distrito",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))-E8-E9`, S.moneda),
    cf(`(C7+D7)-E7`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 8 — FONDO EVANGELISMO
  fila(ws, 7, [
    null,
    cv('FONDO EVANGELISMO', S.desc),
    refC(8, 8),
    cf(`SUMIFS(MONTO,CentroCosto,"Distrito",TipoReg,"Ingreso",TipoMovto,"Evangelismo Distrital",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"Distrito",TipoReg,"Gasto",TipoMovto,"Evangelismo Distrital",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`(C8+D8)-E8`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 9 — Aporte SBN
  fila(ws, 8, [
    null,
    cv('Aporte SBN', S.desc),
    refC(9, 9),
    cf(`SUMIFS(MONTO,CentroCosto,"Distrito",TipoReg,"Ingreso",TipoMovto,"Presupuesto Educación",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"Distrito",TipoReg,"Gasto",TipoMovto,"Presupuesto Educación",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`(C9+D9)-E9`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 10 — TOTALES
  fila(ws, 9, [
    cv('TOTALES', S.totalLbl), null,
    cf(`SUM(C7:C9)`, S.totalVal),
    cf(`SUM(D7:D9)`, S.totalVal),
    cf(`SUM(E7:E9)`, S.totalVal),
    cf(`C10+D10-E10`, S.totalVal),
    null,
  ]);
  addMerges(ws, [[9, 0, 9, 1]]);

  // ── Sección MNI (fila 12 = índice 11) ──
  fila(ws, 11, [ cv('MNI DISTRITO BIO BIO', S.seccion) ]);
  addMerges(ws, [[11,0,11,6]]);
  fila(ws, 12, [
    null, null,
    cv('SALDO MES ANTERIOR', S.thCol),
    cv('INGRESOS', S.thCol),
    cv('GASTOS', S.thCol),
    cv('SALDO', S.thCol),
    cv('OBSERVACION DE GASTOS', S.thCol),
  ]);

  const refCMni = (rOrigen) => esPrimerMes
    ? cvc(saldosIniciales.mni[rOrigen] || 0, S.moneda)
    : cf(`'${sheetAnt}'!F${rOrigen}`, S.moneda);

  // Fila 14 — FONDO EVANG. MUNDIAL (FEM)
  fila(ws, 13, [
    null,
    cv('FONDO EVANG. MUNDIAL (FEM)', S.desc),
    refCMni(14),
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoReg,"Ingreso",TipoMovto,"FEM",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoReg,"Gasto",TipoMovto,"FEM",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`+C14+D14-E14`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 15 — PPTO DISTRITAL MNI
  fila(ws, 14, [
    null,
    cv('PPTO DISTRITAL MNI', S.desc),
    refCMni(15),
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoReg,"Ingreso",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoReg,"Gasto",TipoMovto,"Ofrenda misioneros",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`(C15+D15)-E15`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 16 — ALABASTRO
  fila(ws, 15, [
    null,
    cv('ALABASTRO', S.desc),
    refCMni(16),
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoReg,"Ingreso",TipoMovto,"ALABASTRO",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoReg,"Gasto",TipoMovto,"ALABASTRO",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`(C16+D16)-E16`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 17 — TOTALES MNI
  fila(ws, 16, [
    cv('TOTALES', S.totalLbl), null,
    cf(`SUM(C14:C16)`, S.totalVal),
    cf(`SUM(D14:D16)`, S.totalVal),
    cf(`SUM(E14:E16)`, S.totalVal),
    cf(`C17+D17-E17`, S.totalVal),
    null,
  ]);
  addMerges(ws, [[16, 0, 16, 1]]);

  // ── Sección DNI (fila 19 = índice 18) ──
  fila(ws, 18, [ cv('DNI DISTRITO BIO BIO', S.seccion) ]);
  addMerges(ws, [[18,0,18,6]]);
  fila(ws, 19, [
    null,
    cv('SALDOS MES ANTERIOR', S.thCol), null,
    cv('INGRESOS', S.thCol),
    cv('GASTOS', S.thCol),
    cv('SALDO', S.thCol),
    cv('OBSERVACION DE GASTOS', S.thCol),
  ]);
  addMerges(ws, [[19, 1, 19, 2]]);

  const refCDni = (rOrigen) => esPrimerMes
    ? cvc(saldosIniciales.dni[rOrigen] || 0, S.moneda)
    : cf(`'${sheetAnt}'!F${rOrigen}`, S.moneda);

  // Fila 21 — PPTO DISTRITAL DNI
  fila(ws, 20, [
    null,
    cv('PPTO DISTRITAL DNI', S.desc),
    refCDni(21),
    cf(`SUMIFS(MONTO,CentroCosto,"DNI",TipoReg,"Ingreso",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"DNI",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`C21+D21-E21`, S.moneda),
    cv('', S.obs),
  ]);
  // Filas 22-23 (índices 21-22): líneas en blanco con saldo formulado
  fila(ws, 21, [
    null, cv('', S.desc),
    refCDni(22),
    cvc(0, S.moneda),
    cvc(0, S.moneda),
    cf(`(C22+D22)-E22`, S.moneda),
    cv('', S.obs),
  ]);
  fila(ws, 22, [
    null, cv('', S.desc),
    refCDni(23),
    cvc(0, S.moneda),
    cvc(0, S.moneda),
    cf(`(C23+D23)-E23`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 24 — TOTALES DNI
  fila(ws, 23, [
    cv('TOTALES', S.totalLbl), null,
    cf(`SUM(C21:C23)`, S.totalVal),
    cf(`SUM(D21:D23)`, S.totalVal),
    cf(`SUM(E21:E23)`, S.totalVal),
    cf(`C24+D24-E24`, S.totalVal),
    null,
  ]);
  addMerges(ws, [[23, 0, 23, 1]]);

  // ── Sección JNI (fila 26 = índice 25) ──
  fila(ws, 25, [ cv('JNI DISTRITO BIO BIO', S.seccion) ]);
  addMerges(ws, [[25,0,25,6]]);
  fila(ws, 26, [
    null,
    cv('SALDO MES ANTERIOR', S.thCol), null,
    cv('INGRESOS', S.thCol),
    cv('GASTOS', S.thCol),
    cv('SALDO', S.thCol),
    cv('OBSERVACION DE GASTOS', S.thCol),
  ]);
  addMerges(ws, [[26, 1, 26, 2]]);

  const refCJni = (rOrigen) => esPrimerMes
    ? cvc(saldosIniciales.jni[rOrigen] || 0, S.moneda)
    : cf(`+'${sheetAnt}'!F${rOrigen}`, S.moneda);

  // Fila 28 — PPTO DISTRITAL JNI
  fila(ws, 27, [
    null,
    cv('PPTO DISTRITAL JNI', S.desc),
    refCJni(28),
    cf(`SUMIFS(MONTO,CentroCosto,"JNI",TipoReg,"Ingreso",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`SUMIFS(MONTO,CentroCosto,"JNI",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    cf(`C28+D28-E28`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 29 — línea en blanco
  fila(ws, 28, [
    null, cv('', S.desc),
    refCJni(29),
    cvc(0, S.moneda),
    cvc(0, S.moneda),
    cf(`(C29+D29)-E29`, S.moneda),
    cv('', S.obs),
  ]);
  // Fila 30 — TOTALES JNI
  fila(ws, 29, [
    cv('TOTALES', S.totalLbl), null,
    cf(`SUM(C28:C29)`, S.totalVal),
    cf(`SUM(D28:D29)`, S.totalVal),
    cf(`SUM(E28:E29)`, S.totalVal),
    cf(`C30+D30-E30`, S.totalVal),
    null,
  ]);
  addMerges(ws, [[29, 0, 29, 1]]);

  // ── RESUMEN (filas 34-38) ──
  fila(ws, 33, [ null, cv('RESUMEN', S.seccion) ]);
  addMerges(ws, [[33, 1, 33, 5]]);

  const mesAntLbl = mesAntKey
    ? `${NOMBRES_CORTOS[+mesAntKey.split('-')[1]-1]} ${mesAntKey.split('-')[0]}`
    : 'ANTERIOR';

  fila(ws, 34, [
    null,
    cv(`SALDO MES ${mesAntLbl}`, S.resumenLbl),
    esPrimerMes
      ? cvc(saldosIniciales.resumen || 0, S.resumenVal)
      : cf(`+'${sheetAnt}'!C38`, S.resumenVal),
  ]);
  addMerges(ws, [[34, 1, 34, 1]]);

  fila(ws, 35, [
    null,
    cv('TOTAL INGRESOS DEL MES', S.resumenLbl),
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.resumenVal),
  ]);
  fila(ws, 36, [
    null,
    cv('TOTAL EGRESOS DEL MES', S.resumenLbl),
    cf(`SUMIFS(MONTO,TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.resumenVal),
  ]);
  fila(ws, 37, [
    null,
    cv(`SALDO ACTUAL al mes de ${NOMBRES_MESES_FULL[mes-1]} ${anio}`, S.resumenLbl),
    cf(`+C35+C36-C37`, S.resumenVal),
  ]);

  // ── Firma (justo después del resumen para que entre todo en una sola página) ──
  fila(ws, 40, [ null, cv(`INFORME AL CIERRE DE ${NOMBRES_MESES_FULL[mes-1]} ${anio}`, S.firma) ]);
  fila(ws, 41, [ null, cv('GERALDINE CLAVERIA S.', S.firma) ]);
  fila(ws, 42, [ null, cv('TESORERA DISTRITO BIO BIO', S.firma) ]);
  addMerges(ws, [[40,1,40,5],[41,1,41,5],[42,1,42,5]]);

  // Anchos de columna (similares al original).
  // Columnas J-M (índices 9-12) son metadatos para las fórmulas SUMIFS (año/mes/díaInicio/díaFin).
  // Se dejan muy estrechas porque no aportan al informe imprimible.
  ws['!cols'] = [
    { wch: 6 },   // A
    { wch: 42 },  // B
    { wch: 18 },  // C
    { wch: 16 },  // D
    { wch: 16 },  // E
    { wch: 16 },  // F
    { wch: 32 },  // G
    { wch: 2 },   // H — separador
    { wch: 2 },   // I — separador
    { wch: 5 },   // J (año)
    { wch: 4 },   // K (mes)
    { wch: 5 },   // L (pr.dia)
    { wch: 5 },   // M (ult.dia)
  ];

  setRef(ws, 42, 12);
  // scale=65 fuerza que todo el contenido (incluidas las celdas J-M de metadatos) entre
  // en una sola página landscape. printArea no siempre es respetado por LibreOffice/visores
  // alternativos, así que confiamos en el scale.
  setPrint(ws, { scale: 65 });
  return ws;
}

// ═══════════════════════════════════════════════════════════════════════════
//  HOJA MENSUAL IGLESIA — layout dos columnas
// ═══════════════════════════════════════════════════════════════════════════

function construirHojaMensualIglesia(mesKey, mesAntKey, esPrimerMes, saldosIniciales, nomLocal) {
  const ws = { '!merges': [] };
  const [anio, mes] = mesKey.split('-').map(Number);
  const ultDia      = ultimoDia(mesKey);
  const sheetAnt    = mesAntKey ? sheetNameIglesia(mesAntKey) : null;
  const refSheetAnt = sheetAnt ? `'${sheetAnt}'` : null;

  const sAnt = (refF, valDefault) => esPrimerMes
    ? cvc(valDefault || 0, S.moneda)
    : cf(`${refSheetAnt}!${refF}`, S.moneda);

  // ── Filas 1-3: encabezado ──
  fila(ws, 0, [
    cv('INFORME MENSUAL', S.tituloPrincipal),
    null,null,null,null,null,null,null,null,
    cvc(anio, S.encabezadoMiniVal),
    cvc(mes,  S.encabezadoMiniVal),
    cvc(1,    S.encabezadoMiniVal),
    cvc(ultDia, S.encabezadoMiniVal),
  ]);
  fila(ws, 1, [
    cv(`INFORME FINANZAS ${nomLocal.toUpperCase()}`, S.tituloPrincipal),
    null,null,null,null,null,null,null,null,
    cv('AÑO', S.encabezadoMini),
    cv('MES', S.encabezadoMini),
    cv('PR.DIA', S.encabezadoMini),
    cv('ULT. DIA DEL MES', S.encabezadoMini),
  ]);
  fila(ws, 2, [ cv(`MES ${NOMBRES_MESES_FULL[mes-1]} ${anio}`, S.tituloMes) ]);
  addMerges(ws, [[0,0,0,8],[1,0,1,8],[2,0,2,7]]);

  // ── Fila 4 (índice 3): bandas IGLESIA | MNI ──
  fila(ws, 3, [
    cv('IGLESIA', S.seccion), null, null,
    null,
    cv('MISIONES NAZARENAS INTERNACIONALES', S.seccion),
  ]);
  addMerges(ws, [[3,0,3,2],[3,4,3,7]]);

  // ── Fila 5 (índice 4): Saldos mes anterior ──
  fila(ws, 4, [
    cv('Saldo Mes Anterior:                                  (A):', S.descBold), null,
    sAnt('H46', saldosIniciales?.iglesia || 0),
    null,
    cv('Saldo Mes Anterior: ', S.descBold), null,
    cv('(P):', S.descBold),
    sAnt('H49', saldosIniciales?.mni || 0),
  ]);
  addMerges(ws, [[4,0,4,1],[4,4,4,5]]);

  // ── Fila 6 (índice 5): "Entradas del Mes:" ──
  fila(ws, 5, [
    cv('Entradas del Mes:', S.descBold), null, null,
    null,
    cv('Entradas del Mes:', S.descBold),
  ]);
  addMerges(ws, [[5,0,5,2],[5,4,5,7]]);

  // Ingresos iglesia
  fila(ws, 6, [
    cv('01.', S.desc),
    cv('Ofrendas regulares Cultos', S.desc),
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"Iglesia",TipoMovto,"Ofrenda",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('15.  Fondo de Evangelismo Mundial (FEM)', S.desc), null, null,
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"MNI",TipoMovto,"FEM",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  addMerges(ws, [[6,4,6,6]]);
  fila(ws, 7, [
    cv('02.', S.desc),
    cv('Diezmos', S.desc),
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"Iglesia",TipoMovto,"Diezmo",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('17.  Ofrendas Regulares', S.desc), null, null,
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"MNI",TipoMovto,"Ofrenda Regular",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  addMerges(ws, [[7,4,7,6]]);
  fila(ws, 8, [
    cv('04.', S.desc),
    cv('Otras entradas Iglesia', S.desc),
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"Iglesia",TipoMovto,"Kilo de amor",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))+SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"Iglesia",TipoMovto,"Ofrenda Especial",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('18.  Otras Entradas', S.desc), null, null,
    cvc(0, S.moneda),
  ]);
  addMerges(ws, [[8,4,8,6]]);

  // Total Recibido (B) / (Q)
  fila(ws, 9, [
    cv('Total Recibido:                                          (B):', S.descBold), null,
    cf(`SUM(C7:C9)`, S.totalVal),
    null,
    cv('Total Recibido: ', S.descBold), null,
    cv('(Q):', S.descBold),
    cf(`SUM(H7:H8)`, S.totalVal),
  ]);
  addMerges(ws, [[9,0,9,1],[9,4,9,5]]);

  // (R) Total Entradas más Saldos (sólo lado MNI)
  fila(ws, 10, [
    null,null,null,null,
    cv('Total Entradas más Saldos (P+Q) ', S.descBold), null,
    cv(' (R):', S.descBold),
    cf(`H5+H10`, S.totalVal),
  ]);
  addMerges(ws, [[10,4,10,5]]);

  // (C) Total Entradas más Saldos iglesia | "Gastos del Mes:" lado MNI
  fila(ws, 11, [
    cv('Total Entradas más Saldos (A+B)                 (C):', S.descBold), null,
    cf(`C10+C5`, S.totalVal),
    null,
    cv('Gastos del Mes:', S.descBold),
  ]);
  addMerges(ws, [[11,0,11,1],[11,4,11,7]]);

  // Fila 13: "Gastos del Mes:" iglesia | S25 FEM
  fila(ws, 12, [
    cv('Gastos del Mes:', S.descBold), null, null,
    null,
    cv('S25. Fondo de Evangelismo Mundial (FEM) + ALABASTRO', S.desc), null, null,
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoMovto,"FEM",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))+SUMIFS(MONTO,CentroCosto,"MNI",TipoMovto,"ALABASTRO",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  addMerges(ws, [[12,0,12,2],[12,4,12,6]]);

  // Filas 14-22: gastos iglesia (A) | gastos MNI / Min Mujer (E-H)
  fila(ws, 13, [
    cv('S1.', S.desc), cv('Sueldo Pastor', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Sueldo",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('S26. Presupuesto Distrital ', S.desc), null, null,
    cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoMovto,"Presupuesto Distrito",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  addMerges(ws, [[13,4,13,6]]);
  fila(ws, 14, [
    cv('S2.', S.desc), cv('Beneficios Pastorales ', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"AFP",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))+SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Salud",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('S27. Gastos Comunes', S.desc), null, null,
    cf(`(SUMIFS(MONTO,TipoReg,"Gasto",CentroCosto,"MNI",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1)))-(H13+H14+H16)`, S.moneda),
  ]);
  addMerges(ws, [[14,4,14,6]]);
  fila(ws, 15, [
    cv('S3.', S.desc), cv('Indemnización Pastoral ', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Indemnización",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('Ofrenda a MISIONEROS', S.desc), null, null,
    cvc(0, S.moneda),
  ]);
  addMerges(ws, [[15,4,15,6]]);
  fila(ws, 16, [
    cv('S4.', S.desc), cv('Mantención/Reparación/Construcción', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Mantención",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))+SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Reparación",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('Total Gastos: ', S.descBold), null,
    cv('(S):', S.descBold),
    cf(`SUM(H13:H15)`, S.totalVal),
  ]);
  addMerges(ws, [[16,4,16,5]]);
  fila(ws, 17, [
    cv('S5.', S.desc), cv('Gastos Generales', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))-((SUM(C14:C17)+SUM(C19:C21)))`, S.moneda),
    null,
    cv('Saldo al fin de Mes (R-S):', S.descBold), null,
    cv('(T):', S.descBold),
    cf(`H11-H17`, S.totalVal),
  ]);
  addMerges(ws, [[17,4,17,5]]);
  fila(ws, 18, [
    cv('S8.', S.desc), cv('Presupuesto Distrital', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  fila(ws, 19, [
    cv('S9.', S.desc), cv('Presupuesto SBN ', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Presupuesto Educación",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('MINISTERIO DE LA MUJER', S.seccion),
  ]);
  addMerges(ws, [[19,4,19,7]]);
  fila(ws, 20, [
    cv('S13.', S.desc), cv('Vehículo', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Seguros",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))+SUMIFS(MONTO,CentroCosto,"Iglesia",TipoReg,"Gasto",TipoMovto,"Patente",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('Saldo Mes Anterior: ', S.descBold), null,
    cv(' (X0):', S.descBold),
    sAnt('H50', saldosIniciales?.minMujer || 0),
  ]);
  addMerges(ws, [[20,4,20,5]]);

  // (D) Total Gastos iglesia | (X1) entradas Min Mujer
  fila(ws, 21, [
    cv('Total Gastos:                                               (D):', S.descBold), null,
    cf(`SUM(C14:C21)`, S.totalVal),
    null,
    cv('25.     Total Entradas del Mes:', S.descBold), null,
    cv(' (X1):', S.descBold),
    cvc(0, S.moneda),  // informativo; sin SUMIFS específico
  ]);
  addMerges(ws, [[21,0,21,1],[21,4,21,5]]);

  // (E) Saldo iglesia | (X2) total entradas Min Mujer
  fila(ws, 22, [
    cv('Saldo al fin de Mes (C-D):                 (E):', S.descBold), null,
    cf(`C12-C22`, S.totalVal),
    null,
    cv('Total Entradas más Saldos (X0+X1)               ', S.descBold), null,
    cv('  (X2):', S.descBold),
    cf(`+H21+H22`, S.totalVal),
  ]);
  addMerges(ws, [[22,0,22,1],[22,4,22,5]]);

  // Línea vacía 24
  // (X3) gastos Min Mujer
  fila(ws, 24, [
    null,null,null,null,
    cv('S-30. Total Gastos del Mes:', S.descBold), null,
    cv('  (X3):', S.descBold),
    cvc(0, S.moneda),
  ]);
  addMerges(ws, [[24,4,24,5]]);

  // Banda DNI | (X4) saldo Min Mujer
  fila(ws, 25, [
    cv('MINISTERIOS NAZARENOS DE DISCIPULADO (DNI)', S.seccion), null, null,
    null,
    cv('Saldo al fin de Mes (X2-X3):                              ', S.descBold), null,
    cv('(X4):', S.descBold),
    cf(`H23-H25`, S.totalVal),
  ]);
  addMerges(ws, [[25,0,25,2],[25,4,25,5]]);

  // (F) saldo mes ant DNI | banda Compasión
  fila(ws, 26, [
    cv('Saldo Mes Anterior:                                              (F):', S.descBold), null,
    sAnt('H47', saldosIniciales?.dni || 0),
    null,
    cv('MINISTERIO DE COMPASION', S.seccion),
  ]);
  addMerges(ws, [[26,0,26,1],[26,4,26,7]]);

  // "Entradas del Mes:" DNI | (Y0) saldo Compasión
  fila(ws, 27, [
    cv('Entradas del Mes:', S.descBold), null, null,
    null,
    cv('Saldo Mes Anterior: ', S.descBold), null,
    cv('   (Y0):', S.descBold),
    sAnt('H51', saldosIniciales?.compasion || 0),
  ]);
  addMerges(ws, [[27,0,27,2],[27,4,27,5]]);

  // Ingresos DNI | (Y1) entradas Compasión
  fila(ws, 28, [
    cv('05.', S.desc), cv('Ofrendas Regulares', S.desc),
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"DNI",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('Saldo Mes Anterior: ', S.descBold), null,
    cv('   (Y0):', S.descBold),
    sAnt('H51', 0),
  ]);
  addMerges(ws, [[28,4,28,5]]);
  fila(ws, 29, [
    cv('06.', S.desc), cv('Otras Entradas', S.desc),
    cvc(0, S.moneda),
    null,
    cv('35.     Entradas del Mes: ', S.desc), null,
    cv('   (Y1):', S.descBold),
    cf(`SUMIFS(MONTO,CentroCosto,"Compasión",TipoReg,"Ingreso",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  addMerges(ws, [[29,4,29,5]]);

  // (G) Total Recibido DNI | (Y2) total entradas Compasión
  fila(ws, 30, [
    cv('Total Recibido:                                                      (G):', S.descBold), null,
    cf(`SUM(C29:C30)`, S.totalVal),
    null,
    cv('Total Entradas más Saldos (Y0+Y1)', S.descBold), null,
    cv('   (Y2):', S.descBold),
    cf(`H29+H30`, S.totalVal),
  ]);
  addMerges(ws, [[30,0,30,1],[30,4,30,5]]);

  // (Y3) total gastos Compasión
  fila(ws, 31, [
    null,null,null,null,
    cv('S-40.  Total Gastos del Mes: ', S.descBold), null,
    cv('   (Y3):', S.descBold),
    cf(`SUMIFS(MONTO,CentroCosto,"Compasión",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
  ]);
  addMerges(ws, [[31,4,31,5]]);

  // (H) Total entradas + saldos DNI | (Y4) saldo Compasión
  fila(ws, 32, [
    cv('Total Entradas más Saldos (F+G)                         (H):', S.descBold), null,
    cf(`+C27+C31`, S.totalVal),
    null,
    cv('Saldo al fin de Mes (Y2-Y3):', S.descBold), null,
    cv('   (Y4):', S.descBold),
    cf(`H31-H32`, S.totalVal),
  ]);
  addMerges(ws, [[32,0,32,1],[32,4,32,5]]);

  // "Gastos del Mes:" DNI
  fila(ws, 33, [ cv('Gastos del Mes:', S.descBold) ]);
  addMerges(ws, [[33,0,33,2]]);

  // Gastos DNI | banda "RESUMEN MOVIMIENTOS …"
  fila(ws, 34, [
    cv('S14.', S.desc), cv('Gastos Comunes', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"DNI",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))-C36`, S.moneda),
    null,
    cf(`"RESUMEN MOVIMIENTOS "&A3`, S.seccion),
  ]);
  addMerges(ws, [[34,4,34,7]]);

  // Encabezados tabla resumen
  fila(ws, 35, [
    cv('S16.', S.desc), cv('Presupuesto Distrital', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"DNI",TipoReg,"Gasto",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null, null,
    cv('Entradas', S.thCol),
    cv('Gastos', S.thCol),
    cv('Saldos', S.thCol),
  ]);

  // (I) Total Gastos DNI | resumen IGLESIA
  fila(ws, 36, [
    cv('Total Gastos:                                                           (I):', S.descBold), null,
    cf(`C35+C36`, S.totalVal),
    null,
    cv('IGLESIA', S.desc),
    cf(`C10`, S.moneda),
    cf(`+C22`, S.moneda),
    cf(`+F37-G37`, S.moneda),
  ]);
  addMerges(ws, [[36,0,36,1]]);

  // (J) Saldo DNI | resumen DNI
  fila(ws, 37, [
    cv('Saldo al Fin de Mes (H-I):                                      (J):', S.descBold), null,
    cf(`C33-C37`, S.totalVal),
    null,
    cv('DNI', S.desc),
    cf(`+C31`, S.moneda),
    cf(`+C37`, S.moneda),
    cf(`+F38-G38`, S.moneda),
  ]);
  addMerges(ws, [[37,0,37,1]]);

  // resumen JNI (sin lado izquierdo aún)
  fila(ws, 38, [
    null,null,null,null,
    cv('JNI', S.desc),
    cf(`+C45`, S.moneda),
    cf(`+C50`, S.moneda),
    cf(`+F39-G39`, S.moneda),
  ]);

  // Banda JNI | resumen MNI
  fila(ws, 39, [
    cv('JUVENTUD NAZARENA INTERNACIONAL (JNI)', S.seccion), null, null,
    null,
    cv('MNI', S.desc),
    cf(`+H10`, S.moneda),
    cf(`+H17`, S.moneda),
    cf(`+F40-G40`, S.moneda),
  ]);
  addMerges(ws, [[39,0,39,2]]);

  // (K) Saldo ant JNI | resumen MIN.MUJER
  fila(ws, 40, [
    cv('Saldo Mes Anterior:                                               (K):', S.descBold), null,
    sAnt('H48', saldosIniciales?.jni || 0),
    null,
    cv('MIN. MUJER', S.desc),
    cf(`+H22`, S.moneda),
    cf(`+H25`, S.moneda),
    cf(`+F41-G41`, S.moneda),
  ]);
  addMerges(ws, [[40,0,40,1]]);

  // "Entradas del Mes:" JNI | resumen MIN.COMP.
  fila(ws, 41, [
    cv('Entradas del Mes:', S.descBold), null, null,
    null,
    cv('MIN. COMP.', S.desc),
    cf(`+H30`, S.moneda),
    cf(`+H32`, S.moneda),
    cf(`+F42-G42`, S.moneda),
  ]);
  addMerges(ws, [[41,0,41,2]]);

  // Ingreso JNI 07. | TOTALES
  fila(ws, 42, [
    cv('07.', S.desc), cv('Ofrendas Regulares', S.desc),
    cf(`SUMIFS(MONTO,TipoReg,"Ingreso",CentroCosto,"JNI",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('TOTALES', S.totalLbl),
    cf(`SUM(F37:F42)`, S.totalVal),
    cf(`SUM(G37:G42)`, S.totalVal),
    cf(`SUM(H37:H42)`, S.totalVal),
  ]);

  fila(ws, 43, [
    cv('08.', S.desc), cv('Otras Entradas', S.desc),
    cvc(0, S.moneda),
  ]);

  // (L) Total Recibido JNI
  fila(ws, 44, [
    cv('Total Recibido:                                                      (L):', S.descBold), null,
    cf(`C43+C44`, S.totalVal),
    null,
    cv('CAJAS', S.thCol),
    cv('SALDO + ENTRADAS', S.thCol),
    cv('GASTOS MES', S.thCol),
    cv('TOTALES', S.thCol),
  ]);
  addMerges(ws, [[44,0,44,1]]);

  // (M) Total entradas + saldos JNI | resumen IGLESIA cuadro 2
  fila(ws, 45, [
    cv('Total Entradas más Saldos (K+L):                       (M):', S.descBold), null,
    cf(`C41+C45`, S.totalVal),
    null,
    cv('IGLESIA', S.desc),
    cf(`C12`, S.moneda),
    cf(`+C22`, S.moneda),
    cf(`F46-G46`, S.moneda),
  ]);
  addMerges(ws, [[45,0,45,1]]);

  // "Gastos del Mes:" JNI | resumen DNI cuadro 2
  fila(ws, 46, [
    cv('Gastos del Mes:', S.descBold), null, null,
    null,
    cv('DNI', S.desc),
    cf(`C33`, S.moneda),
    cf(`+C37`, S.moneda),
    cf(`F47-G47`, S.moneda),
  ]);
  addMerges(ws, [[46,0,46,2]]);

  // Gastos JNI S17 | resumen JNI cuadro 2
  fila(ws, 47, [
    cv('S17.', S.desc), cv('Gastos Comunes', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"JNI",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))-C49`, S.moneda),
    null,
    cv('JNI', S.desc),
    cf(`C46`, S.moneda),
    cf(`C50`, S.moneda),
    cf(`F48-G48`, S.moneda),
  ]);

  // Gastos JNI S18 | resumen MNI cuadro 2
  fila(ws, 48, [
    cv('S18.', S.desc), cv('Presupuesto Distrital', S.desc),
    cf(`SUMIFS(MONTO,CentroCosto,"JNI",TipoReg,"Gasto",TipoMovto,"Presupuesto Distrito",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, S.moneda),
    null,
    cv('MNI', S.desc),
    cf(`H11`, S.moneda),
    cf(`+H17`, S.moneda),
    cf(`F49-G49`, S.moneda),
  ]);

  // (N) Total Gastos JNI | resumen MIN.MUJER cuadro 2
  fila(ws, 49, [
    cv('Total Gastos:                                                         (N):', S.descBold), null,
    cf(`C48+C49`, S.totalVal),
    null,
    cv('MIN. MUJER', S.desc),
    cf(`H23`, S.moneda),
    cf(`+H25`, S.moneda),
    cf(`+F50-G50`, S.moneda),
  ]);
  addMerges(ws, [[49,0,49,1]]);

  // (O) Saldo JNI | resumen MIN.COMP. cuadro 2
  fila(ws, 50, [
    cv('Saldo al Fin de Mes (M-N):                                   (O):', S.descBold), null,
    cf(`C46-C50`, S.totalVal),
    null,
    cv('MIN. COMP.', S.desc),
    cf(`H31`, S.moneda),
    cf(`+H32`, S.moneda),
    cf(`F51-G51`, S.moneda),
  ]);
  addMerges(ws, [[50,0,50,1]]);

  // Firma | TOTALES cuadro 2
  fila(ws, 51, [
    cv(`Tesorero: GERALDINE CLAVERIA S.`, S.firma), null, null,
    null,
    cv('TOTALES', S.totalLbl),
    cf(`SUM(F46:F51)`, S.totalVal),
    cf(`SUM(G46:G51)`, S.totalVal),
    cf(`SUM(H46:H51)`, S.totalVal),
  ]);
  addMerges(ws, [[51,0,51,2]]);

  fila(ws, 52, [
    cv('Fecha', S.descBold), null,
    cf(`TODAY()`, { ...S.desc, numFmt: 'dd-mm-yyyy' }),
  ]);
  fila(ws, 53, [ cv('FIRMA', S.firma) ]);

  // ── RESUMEN CAJA (filas 60-65) ──
  fila(ws, 59, [ null, cv('RESUMEN CAJA ', {
    font: { name: 'Calibri', sz: 14, bold: true },
    fill: { fgColor: { rgb: 'FFFF00' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { top: BORDE_MEDIO, bottom: BORDE_MEDIO, left: BORDE_MEDIO, right: BORDE_MEDIO },
  }) ]);
  addMerges(ws, [[59, 1, 59, 2]]);

  const resumenCajaLbl = {
    font: { name: 'Calibri', sz: 12, bold: true },
    fill: { fgColor: { rgb: 'FFF2CC' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  };
  const resumenCajaVal = {
    font: { name: 'Calibri', sz: 12, bold: true },
    fill: { fgColor: { rgb: 'FFF2CC' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  };

  fila(ws, 60, [
    null,
    cv(`SALDO CTA CTE MES ${mesAntLabelCorto(mesAntKey)}`, resumenCajaLbl),
    esPrimerMes
      ? cvc(saldosIniciales?.caja || 0, resumenCajaVal)
      : cf(`+${refSheetAnt}!C65`, resumenCajaVal),
  ]);
  fila(ws, 61, [
    null,
    cv('Se recibe un total de                                  (+)', resumenCajaLbl),
    cf(`F43-F41`, resumenCajaVal),
  ]);
  fila(ws, 62, [
    null,
    cv('TOTAL CAJA                                                  (=)', resumenCajaLbl),
    cf(`C61+C62`, resumenCajaVal),
  ]);
  fila(ws, 63, [
    null,
    cv('TOTAL GASTOS                                            (-)', resumenCajaLbl),
    cf(`G43-G41`, resumenCajaVal),
  ]);
  fila(ws, 64, [
    null,
    cv('TOTAL CAJA GENERAL DE IGLESIA         (=)', resumenCajaLbl),
    cf(`C63-C64`, resumenCajaVal),
  ]);

  // ── SE DEBE PAGAR AL DISTRITO ──
  const lblPagar = {
    font: { name: 'Calibri', sz: 10, bold: true },
    fill: { fgColor: { rgb: 'FFFF00' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  };
  const valPagar = {
    font: { name: 'Calibri', sz: 10, bold: true },
    fill: { fgColor: { rgb: 'FFFFCC' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: NF_MONEDA,
    border: { top: BORDE_FINO, bottom: BORDE_FINO, left: BORDE_FINO, right: BORDE_FINO },
  };

  // Layout:
  //   B = origen (IGLESIA Abr'26)
  //   C = concepto (Presupuesto Distrital 14%)   ← columna ancha
  //   D = (separadora, se hace merge B71:D71 para el título)
  //   E = monto                                  ← columna ancha
  // El monto se pone en columna E (índice 4) para que no choque con la D estrecha.

  fila(ws, 70, [ null, cv(`SE DEBE PAGAR AL DISTRITO POR INGRESOS ${NOMBRES_MESES_FULL[mes-1]} ${anio}`, lblPagar) ]);
  addMerges(ws, [[70, 1, 70, 4]]);

  const mesAbrev = `${NOMBRES_CORTOS[mes-1]}' ${String(anio).slice(-2)}`;
  // Cada fila: B=origen, C:D=concepto (merge), E=monto
  fila(ws, 71, [ null, cv(`IGLESIA ${mesAbrev}`, lblPagar), cv('Presupuesto Distrital  (14%)', lblPagar), null, cf(`14%*(C7+C8)`, valPagar) ]);
  fila(ws, 72, [ null, cv(`IGLESIA ${mesAbrev}`, lblPagar), cv('Presupuesto SBN  (2%)', lblPagar),       null, cf(`2%*($C$7+$C$8)`, valPagar) ]);
  fila(ws, 73, [ null, cv(`DNI ${mesAbrev}`,     lblPagar), cv('Presupuesto Distrital  (10%)', lblPagar), null, cf(`10%*C29`, valPagar) ]);
  fila(ws, 74, [ null, cv(`JNI ${mesAbrev}`,     lblPagar), cv('Presupuesto Distrital  (10%)', lblPagar), null, cf(`10%*C43`, valPagar) ]);
  fila(ws, 75, [ null, cv(`MNI ${mesAbrev}`,     lblPagar), cv('S26. Presupuesto Distrital', lblPagar),   null, cf(`+H8*10%`, valPagar) ]);
  fila(ws, 76, [ null, cv(`FEM ${mesAbrev}`,     lblPagar), cv('FEM', lblPagar),                          null, cf(`SUMIFS(MONTO,CentroCosto,"MNI",TipoMovto,"FEM",TipoReg,"Gasto",MES,">="&DATE($J$1,$K$1,$L$1),MES,"<="&DATE($J$1,$K$1,$M$1))`, valPagar) ]);
  // Merges C:D para concepto
  addMerges(ws, [[71,2,71,3],[72,2,72,3],[73,2,73,3],[74,2,74,3],[75,2,75,3],[76,2,76,3]]);
  fila(ws, 77, [
    null, null,
    cv('TOTAL A PAGAR AL DISTRITO', {
      ...lblPagar,
      font: { ...lblPagar.font, sz: 11 },
    }),
    null,
    cf(`SUM(E72:E77)`, {
      ...valPagar,
      font: { ...valPagar.font, sz: 11 },
      fill: { fgColor: { rgb: 'E7E6E6' } },
    }),
  ]);
  addMerges(ws, [[77, 2, 77, 3]]);

  // Anchos de columna (similares al original).
  // Columnas J-M (índices 9-12) son metadatos para fórmulas SUMIFS — muy estrechos.
  ws['!cols'] = [
    { wch: 6 },    // A
    { wch: 42 },   // B
    { wch: 16 },   // C
    { wch: 3 },    // D — separador vertical
    { wch: 32 },   // E
    { wch: 16 },   // F
    { wch: 10 },   // G
    { wch: 14 },   // H
    { wch: 2 },    // I
    { wch: 5 },    // J (año)
    { wch: 4 },    // K (mes)
    { wch: 5 },    // L (pr.dia)
    { wch: 5 },    // M (ult.dia)
  ];

  setRef(ws, 77, 12);
  setPrint(ws, { scale: 55 });
  return ws;
}

function mesAntLabelCorto(mesAntKey) {
  if (!mesAntKey) return 'ANTERIOR';
  const [y, m] = mesAntKey.split('-');
  return `${NOMBRES_CORTOS[+m-1]} ${y}`;
}

// ═══════════════════════════════════════════════════════════════════════════
//  HOJAS AUXILIARES (las que ya tenías, condensadas)
// ═══════════════════════════════════════════════════════════════════════════

function construirHojaAportes(grupos, locales, aportesIglesias) {
  const ws = { '!merges': [] };
  let r = 0;
  fila(ws, r, [ cv('APORTES REGISTRADOS EN EL AÑO ECLESIÁSTICO', S.tituloAux) ]);
  addMerges(ws, [[r, 0, r, grupos.length + 1]]);
  r += 2;

  fila(ws, r, [
    cv('IGLESIAS', S.thAux),
    ...grupos.map(g => cv(g.Nombre, S.thAux)),
    cv('Total general', S.thAux),
  ]);
  r++;

  const filaIni = r + 1;
  const localesConAp = locales.filter(l => aportesIglesias[l.CodLocal] && l.CodLocal !== 'DB');

  localesConAp.forEach((loc) => {
    const ap = aportesIglesias[loc.CodLocal] || {};
    const vals = grupos.map(g => ap[g.id] || 0);
    const colB = XLSX.utils.encode_col(1) + (r + 1);
    const colLast = XLSX.utils.encode_col(grupos.length) + (r + 1);
    fila(ws, r, [
      cv(loc.Nombre, S.tdAux),
      ...vals.map(v => cvc(v, S.tdAuxMoney)),
      cf(`SUM(${colB}:${colLast})`, S.tdAuxTotal),
    ]);
    r++;
  });

  const filaFin = r;
  fila(ws, r, [
    cv('Total general', S.tdAuxTotalLbl),
    ...grupos.map((_, i) => {
      const col = XLSX.utils.encode_col(1 + i);
      return cf(`SUM(${col}${filaIni}:${col}${filaFin})`, S.tdAuxTotal);
    }),
    cf(`SUM(${XLSX.utils.encode_col(1 + grupos.length)}${filaIni}:${XLSX.utils.encode_col(1 + grupos.length)}${filaFin})`, S.tdAuxTotal),
  ]);

  ws['!cols'] = [{ wch: 28 }, ...grupos.map(() => ({ wch: 15 })), { wch: 16 }];
  setRef(ws, r, grupos.length + 1);
  setPrint(ws);
  return ws;
}

function construirHojaHistoricoDistrito(grupos, mensualPorGrupo) {
  const ws = { '!merges': [] };
  let r = 0;
  const mesesOrden = sortMeses(Object.keys(mensualPorGrupo));

  fila(ws, r, [ cv('HISTÓRICO DISTRITO BÍO BÍO', S.tituloAux) ]);
  addMerges(ws, [[r, 0, r, 4]]);
  r += 2;

  const gruposOrden = ['DISTR','MNI','DNI','JNI']
    .map(cod => grupos.find(g => g.CodCosto === cod) || (cod === 'DISTR' && grupos.find(g => g.Nombre === 'Distrito')))
    .filter(Boolean);

  gruposOrden.forEach((g) => {
    fila(ws, r, [ cv(g.Nombre.toUpperCase(), S.seccionAux) ]);
    addMerges(ws, [[r, 0, r, 4]]);
    r++;
    fila(ws, r, [
      cv('MES', S.thAux),
      cv('SALDO ANTERIOR', S.thAux),
      cv('INGRESOS', S.thAux),
      cv('GASTOS', S.thAux),
      cv('SALDO', S.thAux),
    ]);
    r++;

    const filaIni = r + 1;
    let saldoAcum = 0;
    mesesOrden.forEach((mes) => {
      const d = mensualPorGrupo[mes]?.[g.id] || { ingresos: 0, gastos: 0 };
      const ing = d.ingresos, gas = d.gastos;
      const sal = saldoAcum + ing - gas;
      const aB = cell(r, 1), aC = cell(r, 2), aD = cell(r, 3);
      fila(ws, r, [
        cv(mesLabel(mes), S.tdAux),
        cvc(saldoAcum, S.tdAuxMoney),
        cvc(ing, S.tdAuxMoney),
        cvc(gas, S.tdAuxMoney),
        cf(`${aB}+${aC}-${aD}`, S.tdAuxMoney),
      ]);
      saldoAcum = sal;
      r++;
    });
    const filaFin = r;
    fila(ws, r, [
      cv('TOTAL', S.tdAuxTotalLbl),
      null,
      cf(`SUM(C${filaIni}:C${filaFin})`, S.tdAuxTotal),
      cf(`SUM(D${filaIni}:D${filaFin})`, S.tdAuxTotal),
      cf(`SUM(E${filaIni}:E${filaFin})`, S.tdAuxTotal),
    ]);
    r += 2;
  });

  ws['!cols'] = [{wch:12},{wch:18},{wch:18},{wch:18},{wch:18}];
  setRef(ws, r, 4);
  setPrint(ws);
  return ws;
}

function construirHojaHistoricoIglesia(mensualPorLocal, localSeleccionado, nomLocal) {
  const ws = { '!merges': [] };
  let r = 0;
  const mesesOrden = sortMeses(Object.keys(mensualPorLocal));

  fila(ws, r, [ cv(`HISTÓRICO — ${nomLocal.toUpperCase()}`, S.tituloAux) ]);
  addMerges(ws, [[r, 0, r, 3]]);
  r += 2;

  fila(ws, r, [
    cv('MES', S.thAux),
    cv('INGRESOS', S.thAux),
    cv('GASTOS', S.thAux),
    cv('SALDO', S.thAux),
  ]);
  r++;

  const filaIni = r + 1;
  mesesOrden.forEach((mes) => {
    const d = mensualPorLocal[mes]?.[localSeleccionado] || { ingresos: 0, gastos: 0 };
    const aB = cell(r, 1), aC = cell(r, 2);
    fila(ws, r, [
      cv(mesLabel(mes), S.tdAux),
      cvc(d.ingresos, S.tdAuxMoney),
      cvc(d.gastos, S.tdAuxMoney),
      cf(`${aB}-${aC}`, S.tdAuxMoney),
    ]);
    r++;
  });
  const filaFin = r;
  fila(ws, r, [
    cv('TOTAL', S.tdAuxTotalLbl),
    cf(`SUM(B${filaIni}:B${filaFin})`, S.tdAuxTotal),
    cf(`SUM(C${filaIni}:C${filaFin})`, S.tdAuxTotal),
    cf(`SUM(D${filaIni}:D${filaFin})`, S.tdAuxTotal),
  ]);

  ws['!cols'] = [{wch:12},{wch:18},{wch:18},{wch:18}];
  setRef(ws, r, 3);
  setPrint(ws);
  return ws;
}

function construirHojaMotivos(motivos, titulo) {
  const ws = { '!merges': [] };
  let r = 0;

  fila(ws, r, [ cv(titulo, S.tituloAux) ]);
  addMerges(ws, [[r, 0, r, 2]]);
  r += 2;

  [
    { label: 'INGRESOS POR MOTIVO', key: 'ingresos' },
    { label: 'GASTOS POR MOTIVO',   key: 'gastos'   },
  ].forEach(({ label, key }) => {
    fila(ws, r, [ cv(label, S.seccionAux) ]);
    addMerges(ws, [[r, 0, r, 2]]);
    r++;
    fila(ws, r, [
      cv('MOTIVO', S.thAux),
      cv('MONTO', S.thAux),
      cv('%', S.thAux),
    ]);
    r++;

    const items = Object.entries(motivos)
      .filter(([, v]) => v[key] > 0)
      .sort((a, b) => b[1][key] - a[1][key]);

    const filaIni = r + 1;
    items.forEach(([mot, v]) => {
      const miCelda = cell(r, 1);
      const rngTotal = `B${filaIni}:B${filaIni + items.length - 1}`;
      fila(ws, r, [
        cv(mot, S.tdAux),
        cvc(v[key], S.tdAuxMoney),
        cf(`${miCelda}/SUM(${rngTotal})`, S.porcentaje),
      ]);
      r++;
    });
    const filaFin = r;
    fila(ws, r, [
      cv('TOTAL', S.tdAuxTotalLbl),
      cf(`SUM(B${filaIni}:B${filaFin})`, S.tdAuxTotal),
      null,
    ]);
    r += 2;
  });

  ws['!cols'] = [{wch:34},{wch:18},{wch:10}];
  setRef(ws, r, 2);
  setPrint(ws);
  return ws;
}

// ═══════════════════════════════════════════════════════════════════════════
//  GENERADORES PRINCIPALES
// ═══════════════════════════════════════════════════════════════════════════

async function generarExcelDistrito({ grupos, locales, mensualPorGrupo, aportesIglesias, motivosGlobal, registros, mesesAGenerar, labelArchivo, saldos }) {
  const wb = XLSX.utils.book_new();

  // Filtrar registros del distrito (todos los CentroCosto del distrito)
  const registrosDistrito = registros.filter(r =>
    ['Distrito', 'MNI', 'DNI', 'JNI'].includes(r.centroCosto)
  );

  // Mapear los saldos del panel (keys planas) a la estructura interna por número de fila
  const s = saldos || SALDOS_DEFAULT_DISTRITO;
  const saldosIniciales = {
    distrito: { 7:  +s.distrito_c7 || 0, 8:  +s.distrito_c8 || 0, 9:  +s.distrito_c9 || 0 },
    mni:      { 14: +s.mni_c14   || 0, 15: +s.mni_c15   || 0, 16: +s.mni_c16   || 0 },
    dni:      { 21: +s.dni_c21   || 0, 22: +s.dni_c22   || 0, 23: +s.dni_c23   || 0 },
    jni:      { 28: +s.jni_c28   || 0, 29: +s.jni_c29   || 0 },
    resumen:  +s.resumen || 0,
  };

  mesesAGenerar.forEach((mesKey, idx) => {
    const mesAnt = idx > 0 ? mesesAGenerar[idx - 1] : null;
    const esPrimerMes = idx === 0;
    const ws = construirHojaMensualDistrito(mesKey, mesAnt, esPrimerMes, saldosIniciales);
    XLSX.utils.book_append_sheet(wb, ws, sheetNameDistrito(mesKey));
  });

  // ── Hojas auxiliares ──
  XLSX.utils.book_append_sheet(wb, construirHojaAportes(grupos, locales, aportesIglesias), 'Aportes Iglesias');
  XLSX.utils.book_append_sheet(wb, construirHojaHistoricoDistrito(grupos, mensualPorGrupo), 'Histórico');
  XLSX.utils.book_append_sheet(wb, construirHojaMotivos(motivosGlobal, 'DESGLOSE POR MOTIVO — DISTRITO BÍO BÍO'), 'Motivos');

  // ── Hoja REgistrosMes ──
  XLSX.utils.book_append_sheet(wb, construirHojaRegistros(registrosDistrito), 'REgistrosMes');

  // ── Nombres con rango ──
  wb.Workbook = wb.Workbook || {};
  wb.Workbook.Names = nombresDeRangoRegistros(registrosDistrito.length);

  await descargarConPrintSetup(wb, `Informe_Distrito_BioBio_${labelArchivo}.xlsx`);
}

async function generarExcelIglesia({ locales, mensualPorLocal, motivosPorLocal, registros, mesesAGenerar, labelArchivo, localSeleccionado, saldos }) {
  const wb = XLSX.utils.book_new();
  const loc = locales.find(l => l.CodLocal === localSeleccionado);
  const nomLocal = loc?.Nombre || localSeleccionado;
  const motLocal = motivosPorLocal[localSeleccionado] || {};

  // Filtrar registros del local
  const registrosLocal = registros.filter(r => r.codLocal === localSeleccionado);

  const s = saldos || SALDOS_DEFAULT_IGLESIA;
  const saldosIniciales = {
    iglesia:   +s.iglesia   || 0,
    mni:       +s.mni       || 0,
    dni:       +s.dni       || 0,
    jni:       +s.jni       || 0,
    minMujer:  +s.minMujer  || 0,
    compasion: +s.compasion || 0,
    caja:      +s.caja      || 0,
  };

  mesesAGenerar.forEach((mesKey, idx) => {
    const mesAnt = idx > 0 ? mesesAGenerar[idx - 1] : null;
    const esPrimerMes = idx === 0;
    const ws = construirHojaMensualIglesia(mesKey, mesAnt, esPrimerMes, saldosIniciales, nomLocal);
    XLSX.utils.book_append_sheet(wb, ws, sheetNameIglesia(mesKey));
  });

  XLSX.utils.book_append_sheet(wb, construirHojaHistoricoIglesia(mensualPorLocal, localSeleccionado, nomLocal), 'Histórico');
  XLSX.utils.book_append_sheet(wb, construirHojaMotivos(motLocal, `DESGLOSE POR MOTIVO — ${nomLocal.toUpperCase()}`), 'Motivos');
  XLSX.utils.book_append_sheet(wb, construirHojaRegistros(registrosLocal), 'REgistrosMes');

  wb.Workbook = wb.Workbook || {};
  wb.Workbook.Names = nombresDeRangoRegistros(registrosLocal.length);

  await descargarConPrintSetup(wb, `Informe_${nomLocal.replace(/\s+/g,'_')}_${labelArchivo}.xlsx`);
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMPONENTE REACT
// ═══════════════════════════════════════════════════════════════════════════

export default function Informes() {
  const { usuario } = useAuth();
  const toast = useToast();

  const [tipo, setTipo] = useState(null);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [mesDistrito,        setMesDistrito]        = useState('');
  const [mesIglesia,         setMesIglesia]          = useState('');
  const [localIglesia,       setLocalIglesia]        = useState('');

  // ── Período ──
  const [periodoDistrito,    setPeriodoDistrito]     = useState('mes');   // 'mes' | 'anual' | 'rango'
  const [periodoIglesia,     setPeriodoIglesia]      = useState('mes');
  const [anioEclesiasticoD,  setAnioEclesiasticoD]  = useState('');      // año inicio del año ecl. p.ej. "2025"
  const [anioEclesiasticoI,  setAnioEclesiasticoI]  = useState('');
  const [fechaInicioD,       setFechaInicioD]        = useState('');      // "YYYY-MM"
  const [fechaFinD,          setFechaFinD]           = useState('');
  const [fechaInicioI,       setFechaInicioI]        = useState('');
  const [fechaFinI,          setFechaFinI]           = useState('');

  // ── Saldos iniciales del año eclesiástico ──
  const [mostrarSaldosD, setMostrarSaldosD] = useState(false);
  const [mostrarSaldosI, setMostrarSaldosI] = useState(false);
  const [saldosD, setSaldosD] = useState(SALDOS_DEFAULT_DISTRITO);
  const [saldosI, setSaldosI] = useState(SALDOS_DEFAULT_IGLESIA);

  // Año eclesiástico inicio derivado del período/estado actual
  const anioInicioDistrito = (() => {
    if (periodoDistrito === 'mes' && mesDistrito) {
      const [y, m] = mesDistrito.split('-').map(Number);
      return m >= 10 ? y : y - 1;
    }
    if (periodoDistrito === 'anual' && anioEclesiasticoD) return +anioEclesiasticoD;
    if (periodoDistrito === 'rango' && fechaInicioD) {
      const [y, m] = fechaInicioD.split('-').map(Number);
      return m >= 10 ? y : y - 1;
    }
    return null;
  })();
  const anioInicioIglesia = (() => {
    if (periodoIglesia === 'mes' && mesIglesia) {
      const [y, m] = mesIglesia.split('-').map(Number);
      return m >= 10 ? y : y - 1;
    }
    if (periodoIglesia === 'anual' && anioEclesiasticoI) return +anioEclesiasticoI;
    if (periodoIglesia === 'rango' && fechaInicioI) {
      const [y, m] = fechaInicioI.split('-').map(Number);
      return m >= 10 ? y : y - 1;
    }
    return null;
  })();

  // Cargar saldos guardados cuando cambia año/local
  useEffect(() => {
    if (anioInicioDistrito) setSaldosD(leerSaldosDistrito(anioInicioDistrito));
  }, [anioInicioDistrito]);

  useEffect(() => {
    if (anioInicioIglesia && localIglesia) setSaldosI(leerSaldosIglesia(anioInicioIglesia, localIglesia));
  }, [anioInicioIglesia, localIglesia]);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const res = await api.get('/documentos/informes');
      if (res.data.success) {
        const d = res.data.data;
        setDatos(d);
        const mD = sortMeses(Object.keys(d.mensualPorGrupo));
        const mI = sortMeses(Object.keys(d.mensualPorLocal));
        if (mD.length) {
          const ultimo = mD[mD.length - 1];
          setMesDistrito(ultimo);
          setFechaInicioD(mD[0]);
          setFechaFinD(ultimo);
          const [y, m] = ultimo.split('-').map(Number);
          setAnioEclesiasticoD(String(m >= 10 ? y : y - 1));
        }
        if (mI.length) {
          const ultimo = mI[mI.length - 1];
          setMesIglesia(ultimo);
          setFechaInicioI(mI[0]);
          setFechaFinI(ultimo);
          const [y, m] = ultimo.split('-').map(Number);
          setAnioEclesiasticoI(String(m >= 10 ? y : y - 1));
        }
        const lUsu = d.locales.find(l => l.CodLocal === usuario?.Local && l.CodLocal !== 'DB');
        const lDef = lUsu?.CodLocal || d.locales.find(l => l.CodLocal !== 'DB')?.CodLocal;
        if (lDef) setLocalIglesia(lDef);
      }
    } catch (e) {
      toast.error('Error al cargar datos de informes');
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, [usuario, toast]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // Calcula la lista de claves "YYYY-MM" a incluir según el período activo
  const getMesesAGenerar = (periodo, mes, anioEcl, fechaInicio, fechaFin, todosMeses) => {
    if (periodo === 'mes') {
      if (!mes) return [];
      const [y, m] = mes.split('-').map(Number);
      const anioInicio = m >= 10 ? y : y - 1;
      return mesesAnioEclesiastico(anioInicio).filter(k => k <= mes);
    }
    if (periodo === 'anual') {
      if (!anioEcl) return [];
      return mesesAnioEclesiastico(+anioEcl);
    }
    if (periodo === 'rango') {
      if (!fechaInicio || !fechaFin) return [];
      return todosMeses.filter(k => k >= fechaInicio && k <= fechaFin);
    }
    return [];
  };

  const getLabelArchivo = (periodo, mes, anioEcl, fechaInicio, fechaFin) => {
    if (periodo === 'mes')    return mesLabel(mes).replace(' ', '_');
    if (periodo === 'anual')  return `AE_${anioEcl}-${+anioEcl + 1}`;
    if (periodo === 'rango')  return `${fechaInicio}_al_${fechaFin}`;
    return '';
  };

  const kpisDistrito = () => {
    if (!datos) return null;
    const meses = getMesesAGenerar(periodoDistrito, mesDistrito, anioEclesiasticoD, fechaInicioD, fechaFinD, mesesD);
    if (!meses.length) return null;
    let ing = 0, gas = 0;
    meses.forEach(mes => {
      const m = datos.mensualPorGrupo[mes] || {};
      datos.grupos.forEach(g => { ing += m[g.id]?.ingresos || 0; gas += m[g.id]?.gastos || 0; });
    });
    return { ingresos: ing, gastos: gas, saldo: ing - gas };
  };

  const kpisIglesia = () => {
    if (!datos || !localIglesia) return null;
    const meses = getMesesAGenerar(periodoIglesia, mesIglesia, anioEclesiasticoI, fechaInicioI, fechaFinI, mesesI);
    if (!meses.length) return null;
    let ing = 0, gas = 0;
    meses.forEach(mes => {
      const d = datos.mensualPorLocal[mes]?.[localIglesia] || { ingresos: 0, gastos: 0 };
      ing += d.ingresos; gas += d.gastos;
    });
    return { ingresos: ing, gastos: gas, saldo: ing - gas };
  };

  // ── Handlers para inputs de saldos ──
  const updateSaldoD = (key, valor) => {
    const v = valor === '' ? 0 : Number(valor.replace(/[^\d.-]/g,''));
    const nuevo = { ...saldosD, [key]: v };
    setSaldosD(nuevo);
    if (anioInicioDistrito) guardarSaldosDistrito(anioInicioDistrito, nuevo);
  };
  const updateSaldoI = (key, valor) => {
    const v = valor === '' ? 0 : Number(valor.replace(/[^\d.-]/g,''));
    const nuevo = { ...saldosI, [key]: v };
    setSaldosI(nuevo);
    if (anioInicioIglesia && localIglesia) guardarSaldosIglesia(anioInicioIglesia, localIglesia, nuevo);
  };

  const exportarDistrito = async () => {
    if (!datos) { toast.warning('No hay datos cargados'); return; }
    const meses = getMesesAGenerar(periodoDistrito, mesDistrito, anioEclesiasticoD, fechaInicioD, fechaFinD, mesesD);
    if (!meses.length) { toast.warning('Selecciona un período válido'); return; }
    if (!datos.registros || datos.registros.length === 0) {
      toast.warning('No hay registros para generar las fórmulas SUMIFS'); return;
    }
    setExportando(true);
    try {
      await generarExcelDistrito({
        grupos:          datos.grupos,
        locales:         datos.locales,
        mensualPorGrupo: datos.mensualPorGrupo,
        aportesIglesias: datos.aportesIglesias,
        motivosGlobal:   datos.motivosGlobal,
        registros:       datos.registros,
        mesesAGenerar:   meses,
        labelArchivo:    getLabelArchivo(periodoDistrito, mesDistrito, anioEclesiasticoD, fechaInicioD, fechaFinD),
        saldos:          saldosD,
      });
      toast.success('Informe Distrito exportado');
    } catch (e) {
      toast.error('Error: ' + e.message); console.error(e);
    } finally { setExportando(false); }
  };

  const exportarIglesia = async () => {
    if (!datos || !localIglesia) { toast.warning('Selecciona una iglesia'); return; }
    const meses = getMesesAGenerar(periodoIglesia, mesIglesia, anioEclesiasticoI, fechaInicioI, fechaFinI, mesesI);
    if (!meses.length) { toast.warning('Selecciona un período válido'); return; }
    if (!datos.registros || datos.registros.length === 0) {
      toast.warning('No hay registros para generar las fórmulas SUMIFS'); return;
    }
    setExportando(true);
    try {
      await generarExcelIglesia({
        locales:           datos.locales,
        mensualPorLocal:   datos.mensualPorLocal,
        motivosPorLocal:   datos.motivosPorLocal,
        registros:         datos.registros,
        mesesAGenerar:     meses,
        labelArchivo:      getLabelArchivo(periodoIglesia, mesIglesia, anioEclesiasticoI, fechaInicioI, fechaFinI),
        localSeleccionado: localIglesia,
        saldos:            saldosI,
      });
      toast.success('Informe Iglesia exportado');
    } catch (e) {
      toast.error('Error: ' + e.message); console.error(e);
    } finally { setExportando(false); }
  };

  const mesesD = datos ? sortMeses(Object.keys(datos.mensualPorGrupo)) : [];
  const mesesI = datos ? sortMeses(Object.keys(datos.mensualPorLocal)) : [];
  const localesI = datos ? datos.locales.filter(l => l.CodLocal !== 'DB') : [];
  const aniosEclesiasticosD = [...new Set(mesesD.map(m => { const [y,mo] = m.split('-').map(Number); return String(mo >= 10 ? y : y - 1); }))].sort();
  const aniosEclesiasticosI = [...new Set(mesesI.map(m => { const [y,mo] = m.split('-').map(Number); return String(mo >= 10 ? y : y - 1); }))].sort();
  const kpisD = kpisDistrito();
  const kpisI = kpisIglesia();

  // ── Estilos inline para el panel de saldos ──
  const panelSaldosStyle = { background: '#F8FAFC', border: '1px solid #D1D5DB', borderRadius: 6, padding: 12, marginTop: 8 };
  const grupoSaldoStyle = { marginBottom: 10 };
  const grupoTituloStyle = { fontWeight: 600, fontSize: 13, color: '#1A3A5C', marginBottom: 4 };
  const inputRowStyle = { display: 'grid', gridTemplateColumns: '1fr 140px', gap: 6, alignItems: 'center', marginBottom: 3 };
  const inputStyle = { padding: '4px 6px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 13, textAlign: 'right' };
  const labelStyle = { fontSize: 12, color: '#374151' };

  return (
    <div className="informes-container">
      <div className="informes-header">
        <div>
          <h2>Módulo de Informes</h2>
          <p>Genera informes financieros en Excel con el formato oficial (SUMIFS reales)</p>
        </div>
        <button className="btn-refresh" onClick={cargarDatos} disabled={cargando} title="Recargar">
          <LuRefreshCw className={cargando ? 'spin' : ''} />
        </button>
      </div>

      {cargando && (
        <div className="informes-loading"><LuLoader className="spin" /> Cargando datos...</div>
      )}

      {!cargando && (
        <div className="informes-tipo-grid">
          <div className={`informes-tipo-card ${tipo === 'distrito' ? 'active' : ''}`} onClick={() => setTipo('distrito')}>
            <LuBuilding2 />
            <div><h3>Distrito Bío Bío</h3><p>Distrito, MNI, DNI, JNI — formato oficial encadenado</p></div>
          </div>
          <div className={`informes-tipo-card ${tipo === 'iglesia' ? 'active' : ''}`} onClick={() => setTipo('iglesia')}>
            <LuChurch />
            <div><h3>Iglesia Local</h3><p>Iglesia + MNI + DNI + JNI + Compasión — layout dos columnas</p></div>
          </div>
        </div>
      )}

      {tipo === 'distrito' && datos && (
        <div className="informes-panel">
          <div className="informes-section">
            <h3><LuCalendarDays /> Parámetros</h3>

            {/* Toggle tipo período */}
            <div className="informes-form-row">
              <label>Período</label>
              <div className="informes-periodo-toggle">
                <button className={periodoDistrito === 'mes'   ? 'active' : ''} onClick={() => setPeriodoDistrito('mes')}>Por mes</button>
                <button className={periodoDistrito === 'anual' ? 'active' : ''} onClick={() => setPeriodoDistrito('anual')}>Año eclesiástico</button>
                <button className={periodoDistrito === 'rango' ? 'active' : ''} onClick={() => setPeriodoDistrito('rango')}>Rango fechas</button>
              </div>
            </div>

            {periodoDistrito === 'mes' && (
              <div className="informes-form-row">
                <label>Mes final (genera desde Oct hasta este mes)</label>
                <select value={mesDistrito} onChange={e => setMesDistrito(e.target.value)}>
                  {mesesD.map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
                </select>
              </div>
            )}

            {periodoDistrito === 'anual' && (
              <div className="informes-form-row">
                <label>Año eclesiástico inicio (Oct YYYY — Sep YYYY+1)</label>
                <select value={anioEclesiasticoD} onChange={e => setAnioEclesiasticoD(e.target.value)}>
                  {aniosEclesiasticosD.map(a => <option key={a} value={a}>{a}–{+a+1}</option>)}
                </select>
              </div>
            )}

            {periodoDistrito === 'rango' && (
              <div className="informes-rango-row">
                <div className="informes-form-row">
                  <label>Desde (mes)</label>
                  <select value={fechaInicioD} onChange={e => setFechaInicioD(e.target.value)}>
                    {mesesD.map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
                  </select>
                </div>
                <div className="informes-form-row">
                  <label>Hasta (mes)</label>
                  <select value={fechaFinD} onChange={e => setFechaFinD(e.target.value)}>
                    {mesesD.filter(m => m >= fechaInicioD).map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Panel de saldos iniciales */}
            <div className="informes-form-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={() => setMostrarSaldosD(v => !v)}
                style={{ background: 'none', border: '1px dashed #1A3A5C', color: '#1A3A5C', padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
              >
                {mostrarSaldosD ? '▼' : '▶'} Saldos iniciales año eclesiástico
                {anioInicioDistrito ? ` ${anioInicioDistrito}-${anioInicioDistrito + 1}` : ''}
                {' '}(se aplican al primer mes: Octubre)
              </button>

              {mostrarSaldosD && (
                <div style={panelSaldosStyle}>
                  <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0, marginBottom: 12 }}>
                    Ingresa los saldos al cierre del año anterior. Se guardan en tu navegador y se aplican como "Saldo Mes Anterior" del primer mes (Octubre {anioInicioDistrito}).
                  </p>

                  <div style={grupoSaldoStyle}>
                    <div style={grupoTituloStyle}>DISTRITO</div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>PPTO Distrito (C7)</span>
                      <input style={inputStyle} type="number" value={saldosD.distrito_c7 || ''} onChange={e => updateSaldoD('distrito_c7', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Fondo Evangelismo (C8)</span>
                      <input style={inputStyle} type="number" value={saldosD.distrito_c8 || ''} onChange={e => updateSaldoD('distrito_c8', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Aporte SBN (C9)</span>
                      <input style={inputStyle} type="number" value={saldosD.distrito_c9 || ''} onChange={e => updateSaldoD('distrito_c9', e.target.value)} placeholder="0" />
                    </div>
                  </div>

                  <div style={grupoSaldoStyle}>
                    <div style={grupoTituloStyle}>MNI</div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>FEM (C14)</span>
                      <input style={inputStyle} type="number" value={saldosD.mni_c14 || ''} onChange={e => updateSaldoD('mni_c14', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>PPTO Distrital MNI (C15)</span>
                      <input style={inputStyle} type="number" value={saldosD.mni_c15 || ''} onChange={e => updateSaldoD('mni_c15', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Alabastro (C16)</span>
                      <input style={inputStyle} type="number" value={saldosD.mni_c16 || ''} onChange={e => updateSaldoD('mni_c16', e.target.value)} placeholder="0" />
                    </div>
                  </div>

                  <div style={grupoSaldoStyle}>
                    <div style={grupoTituloStyle}>DNI</div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>PPTO Distrital DNI (C21)</span>
                      <input style={inputStyle} type="number" value={saldosD.dni_c21 || ''} onChange={e => updateSaldoD('dni_c21', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Línea 2 DNI (C22)</span>
                      <input style={inputStyle} type="number" value={saldosD.dni_c22 || ''} onChange={e => updateSaldoD('dni_c22', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Línea 3 DNI (C23)</span>
                      <input style={inputStyle} type="number" value={saldosD.dni_c23 || ''} onChange={e => updateSaldoD('dni_c23', e.target.value)} placeholder="0" />
                    </div>
                  </div>

                  <div style={grupoSaldoStyle}>
                    <div style={grupoTituloStyle}>JNI</div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>PPTO Distrital JNI (C28)</span>
                      <input style={inputStyle} type="number" value={saldosD.jni_c28 || ''} onChange={e => updateSaldoD('jni_c28', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Línea 2 JNI (C29)</span>
                      <input style={inputStyle} type="number" value={saldosD.jni_c29 || ''} onChange={e => updateSaldoD('jni_c29', e.target.value)} placeholder="0" />
                    </div>
                  </div>

                  <div style={grupoSaldoStyle}>
                    <div style={grupoTituloStyle}>RESUMEN</div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Saldo total año anterior</span>
                      <input style={inputStyle} type="number" value={saldosD.resumen || ''} onChange={e => updateSaldoD('resumen', e.target.value)} placeholder="0" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {kpisD && (
            <div className="informes-kpis">
              <div className="kpi-card ingreso"><span className="kpi-label">Ingresos período</span><span className="kpi-valor">{fmt(kpisD.ingresos)}</span></div>
              <div className="kpi-card gasto"><span className="kpi-label">Gastos período</span><span className="kpi-valor">{fmt(kpisD.gastos)}</span></div>
              <div className={`kpi-card saldo ${kpisD.saldo >= 0 ? 'positivo' : 'negativo'}`}><span className="kpi-label">Saldo final</span><span className="kpi-valor">{fmt(kpisD.saldo)}</span></div>
            </div>
          )}

          <button className="btn-exportar-informe" onClick={exportarDistrito} disabled={exportando}>
            <LuDownload /> {exportando ? 'Generando...' : `Exportar Excel — ${periodoDistrito === 'mes' ? 'Año ecl. hasta ' + mesLabel(mesDistrito) : periodoDistrito === 'anual' ? `AE ${anioEclesiasticoD}–${+anioEclesiasticoD+1}` : `${mesLabel(fechaInicioD)} → ${mesLabel(fechaFinD)}`}`}
          </button>
        </div>
      )}

      {tipo === 'iglesia' && datos && (
        <div className="informes-panel">
          <div className="informes-section">
            <h3><LuCalendarDays /> Parámetros</h3>
            <div className="informes-form-row">
              <label>Iglesia</label>
              <select value={localIglesia} onChange={e => setLocalIglesia(e.target.value)}>
                {localesI.map(l => <option key={l.CodLocal} value={l.CodLocal}>{l.Nombre}</option>)}
              </select>
            </div>
            {/* Toggle tipo período */}
            <div className="informes-form-row">
              <label>Período</label>
              <div className="informes-periodo-toggle">
                <button className={periodoIglesia === 'mes'   ? 'active' : ''} onClick={() => setPeriodoIglesia('mes')}>Por mes</button>
                <button className={periodoIglesia === 'anual' ? 'active' : ''} onClick={() => setPeriodoIglesia('anual')}>Año eclesiástico</button>
                <button className={periodoIglesia === 'rango' ? 'active' : ''} onClick={() => setPeriodoIglesia('rango')}>Rango fechas</button>
              </div>
            </div>

            {periodoIglesia === 'mes' && (
              <div className="informes-form-row">
                <label>Mes final (genera desde Oct hasta este mes)</label>
                <select value={mesIglesia} onChange={e => setMesIglesia(e.target.value)}>
                  {mesesI.map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
                </select>
              </div>
            )}

            {periodoIglesia === 'anual' && (
              <div className="informes-form-row">
                <label>Año eclesiástico inicio (Oct YYYY — Sep YYYY+1)</label>
                <select value={anioEclesiasticoI} onChange={e => setAnioEclesiasticoI(e.target.value)}>
                  {aniosEclesiasticosI.map(a => <option key={a} value={a}>{a}–{+a+1}</option>)}
                </select>
              </div>
            )}

            {periodoIglesia === 'rango' && (
              <div className="informes-rango-row">
                <div className="informes-form-row">
                  <label>Desde (mes)</label>
                  <select value={fechaInicioI} onChange={e => setFechaInicioI(e.target.value)}>
                    {mesesI.map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
                  </select>
                </div>
                <div className="informes-form-row">
                  <label>Hasta (mes)</label>
                  <select value={fechaFinI} onChange={e => setFechaFinI(e.target.value)}>
                    {mesesI.filter(m => m >= fechaInicioI).map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Panel de saldos iniciales iglesia */}
            <div className="informes-form-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={() => setMostrarSaldosI(v => !v)}
                style={{ background: 'none', border: '1px dashed #1A3A5C', color: '#1A3A5C', padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
              >
                {mostrarSaldosI ? '▼' : '▶'} Saldos iniciales año eclesiástico
                {anioInicioIglesia ? ` ${anioInicioIglesia}-${anioInicioIglesia + 1}` : ''}
                {' '}(se aplican al primer mes: Octubre)
              </button>

              {mostrarSaldosI && (
                <div style={panelSaldosStyle}>
                  <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0, marginBottom: 12 }}>
                    Saldos al cierre del año anterior para esta iglesia. Se guardan por iglesia + año en tu navegador.
                  </p>

                  <div style={grupoSaldoStyle}>
                    <div style={grupoTituloStyle}>IGLESIA Y MINISTERIOS</div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Iglesia (A)</span>
                      <input style={inputStyle} type="number" value={saldosI.iglesia || ''} onChange={e => updateSaldoI('iglesia', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>MNI (P)</span>
                      <input style={inputStyle} type="number" value={saldosI.mni || ''} onChange={e => updateSaldoI('mni', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>DNI (F)</span>
                      <input style={inputStyle} type="number" value={saldosI.dni || ''} onChange={e => updateSaldoI('dni', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>JNI (K)</span>
                      <input style={inputStyle} type="number" value={saldosI.jni || ''} onChange={e => updateSaldoI('jni', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Min. Mujer (X0)</span>
                      <input style={inputStyle} type="number" value={saldosI.minMujer || ''} onChange={e => updateSaldoI('minMujer', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Min. Compasión (Y0)</span>
                      <input style={inputStyle} type="number" value={saldosI.compasion || ''} onChange={e => updateSaldoI('compasion', e.target.value)} placeholder="0" />
                    </div>
                    <div style={inputRowStyle}>
                      <span style={labelStyle}>Caja general (Resumen)</span>
                      <input style={inputStyle} type="number" value={saldosI.caja || ''} onChange={e => updateSaldoI('caja', e.target.value)} placeholder="0" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {kpisI && (
            <div className="informes-kpis">
              <div className="kpi-card ingreso"><span className="kpi-label">Ingresos período</span><span className="kpi-valor">{fmt(kpisI.ingresos)}</span></div>
              <div className="kpi-card gasto"><span className="kpi-label">Gastos período</span><span className="kpi-valor">{fmt(kpisI.gastos)}</span></div>
              <div className={`kpi-card saldo ${kpisI.saldo >= 0 ? 'positivo' : 'negativo'}`}><span className="kpi-label">Saldo final</span><span className="kpi-valor">{fmt(kpisI.saldo)}</span></div>
            </div>
          )}

          <button className="btn-exportar-informe" onClick={exportarIglesia} disabled={exportando}>
            <LuDownload /> {exportando ? 'Generando...' : `Exportar Excel — ${periodoIglesia === 'mes' ? 'Año ecl. hasta ' + mesLabel(mesIglesia) : periodoIglesia === 'anual' ? `AE ${anioEclesiasticoI}–${+anioEclesiasticoI+1}` : `${mesLabel(fechaInicioI)} → ${mesLabel(fechaFinI)}`}`}
          </button>
        </div>
      )}

      {!tipo && !cargando && (
        <p className="informes-hint">Selecciona un tipo de informe para comenzar</p>
      )}
    </div>
  );
}